import { useCallback, useState } from 'react';
import { useUpdateResourceAllocation } from '~/modules/resourcing/hooks';

import { useDialogState } from '~/modules/common/hooks';
import { useMeContext } from '~/modules/me';
import {
  getResourceRequestDatesFromProjectDate,
  buildUpdatedScheduleRules,
  getTotalHours,
  getDateRangeFromScheduleRules
} from '~/modules/resourcing/common/util';
import {
  dateToMidnightUTCString,
  mapIsoStringtoUtcObject
} from '~/modules/common/dates/convert';
import useReleaseTaskOwners from '~/modules/resourcing/common/components/ReleaseResourceRequestDialog/hooks/useReleaseTaskOwners';
import useFulfilledResourceAllocationHandlersWithoutResourceRequest from '~/modules/resourcing/common/hooks/useFulfilledResourceAllocationHandlersWithoutResourceRequest';
import { ResourceAllocationStatus } from '~/types';
import useResourceRequestToolbarContext from '~/modules/projects/resourcing-plan/hooks/useResourceRequestToolbarContext';
import { buildResourceAllocation } from '~/modules/resourcing/hooks/useCreateResourceAllocationHandler';
import mergePeriodRulesIntoScheduleRules from '~/modules/resourcing/common/util/mergeScheduleUtil';
import { useGetScheduledHoursForUserAndDateRangeCallback } from '~/modules/resourcing/common/hooks/useGetScheduleHoursForUserAndDateRange';
import { getFilterInput } from './useCompletedProjectResourceAllocations';
import {
  useUpdateCompletedResourceAllocationProjectCacheOnAddWithoutResourceRequest,
  getUpdateCompletedResourceAllocationProjectCacheOnEdit
} from './useUpdateCompletedResourceAllocationCache';

import useMergeResourceAllocation from './useMergeResourceAllocation';

const mapUserToAllocationUser = user => ({
  ...user,
  id: user.id,
  costCurrencyUri: user.currentCostRate
    ? user.currentCostRate.currency.id
    : null,
  costCurrency: user.currentCostRate ? user.currentCostRate.currency : null,
  costRate: user.currentCostRate ? user.currentCostRate.amount : null,
  displayText: user.displayText,
  userUri: user.id,
  user: {
    displayText: user.displayText,
    uri: user.id,
    roles: user.roles,
    slug: user.slug,
    permittedActionUris: user.permittedActionUris || []
  }
});

const useResourceAllocationChartEventHandlers = ({
  project,
  sort,
  chartDateRange,
  showTimeOff,
  showHolidays
}) => {
  const {
    getScheduleHoursForUserAndDateRange
  } = useGetScheduledHoursForUserAndDateRangeCallback();
  const me = useMeContext();
  const { filter, scale } = useResourceRequestToolbarContext();
  const { resourceCostMode } = me;

  const [smartFitResourceAllocation, setSmartFitResourceAllocation] = useState(
    {}
  );
  const filterInput = getFilterInput(filter);
  const limit = 50;

  const {
    onCreateResourceAllocation,
    loading: createAllocationLoading
  } = useFulfilledResourceAllocationHandlersWithoutResourceRequest({
    updateCreateCache: useUpdateCompletedResourceAllocationProjectCacheOnAddWithoutResourceRequest(
      {
        projectUri: project.id,
        chartDateRange,
        limit,
        showTimeOff: Boolean(showTimeOff),
        showHolidays: Boolean(showHolidays),
        filter: filterInput,
        sort,
        resourceCostMode
      }
    ),
    chartDateRange,
    showTimeOff: Boolean(showTimeOff),
    showHolidays: Boolean(showHolidays)
  });

  const { onMergeResourceAllocation } = useMergeResourceAllocation();
  const shouldShowTimeOff = Boolean(showTimeOff);
  const shouldShowHolidays = Boolean(showHolidays);
  const cacheOnEdit = useCallback(
    () =>
      getUpdateCompletedResourceAllocationProjectCacheOnEdit({
        projectUri: project.id,
        sort,
        chartDateRange,
        showTimeOff: shouldShowTimeOff,
        showHolidays: shouldShowHolidays,
        limit,
        filter: filterInput
      }),
    [
      project.id,
      sort,
      chartDateRange,
      shouldShowTimeOff,
      shouldShowHolidays,
      limit,
      filterInput
    ]
  );

  const {
    loading: updateAllocationLoading,
    onUpdateResourceAllocation,
    onPatchResourceAllocation
  } = useUpdateResourceAllocation(cacheOnEdit);

  const { releaseTaskOwners } = useReleaseTaskOwners();

  const {
    open: openSmartFitDialogOrLoadingState,
    closeDialog: closeShowSmartFitDialogOrLoading,
    openDialog: openShowSmartFitDialogOrLoading
  } = useDialogState(false);

  const onSmartFitCancel = useCallback(() => {
    closeShowSmartFitDialogOrLoading();
  }, [closeShowSmartFitDialogOrLoading]);

  const handleUserChange = useCallback(
    ({ user, resourceAllocation }) => {
      setSmartFitResourceAllocation({
        ...resourceAllocation,
        user,
        isNewAllocation: true
      });
      openShowSmartFitDialogOrLoading();
    },
    [openShowSmartFitDialogOrLoading, setSmartFitResourceAllocation]
  );

  const handleUserAndRoleChange = useCallback(
    ({ resourceAllocation, user, role }) => {
      setSmartFitResourceAllocation({
        ...resourceAllocation,
        user,
        role,
        isNewAllocation: true
      });

      openShowSmartFitDialogOrLoading();
    },
    [openShowSmartFitDialogOrLoading]
  );

  const handleResourceChangeAndUpdateResourceAllocation = useCallback(
    ({
      resourceAllocation,
      asOfDate: endDate,
      newResourceUser,
      onDialogClose,
      impactedTasks
    }) => {
      const previousDate = endDate.minus({ days: 1 });

      const updatedResourceAllocation = buildResourceAllocation({
        allocationId: null,
        user: newResourceUser,
        me,
        roleUri: resourceAllocation.requestedRoleUri,
        allocationStatus: ResourceAllocationStatus.Committed,
        load: resourceAllocation.load,
        projectUri: resourceAllocation.projectUri,
        scheduleRules: buildUpdatedScheduleRules({
          start: endDate,
          end: mapIsoStringtoUtcObject(resourceAllocation.endDate),
          scheduleRules: resourceAllocation.scheduleRules,
          defaultScheduleRule: project.defaultScheduleRule
        })
      });

      setSmartFitResourceAllocation({
        ...resourceAllocation,
        ...updatedResourceAllocation,
        user: newResourceUser,
        isAllocated: true,
        asOfAllocation: {
          onDialogClose,
          patchAllocation: {
            ...resourceAllocation,
            endDate: previousDate.toISO(),
            scheduleRules: buildUpdatedScheduleRules({
              start: mapIsoStringtoUtcObject(resourceAllocation.startDate),
              end: previousDate,
              scheduleRules: resourceAllocation.scheduleRules,
              defaultScheduleRule: project.defaultScheduleRule
            })
          },
          newAllocation: updatedResourceAllocation
        },
        impactedTasks
      });

      openShowSmartFitDialogOrLoading();
    },
    [me, openShowSmartFitDialogOrLoading, project.defaultScheduleRule]
  );

  const handleAddResourceAllocation = useCallback(
    async ({
      projectDetail: {
        id: projectUri,
        defaultScheduleRule,
        startDate: projectStartDate,
        endDate: projectEndDate
      },
      resourceAllocation,
      overrideScheduleRules = null
    }) => {
      const {
        requestStartDate: start,
        requestEndDate: end
      } = getResourceRequestDatesFromProjectDate({
        projectStartDate,
        projectEndDate,
        scale
      });

      await onCreateResourceAllocation({
        allocation: buildResourceAllocation({
          allocationId: smartFitResourceAllocation.id || null,
          user: resourceAllocation.user,
          me,
          roleUri: resourceAllocation.role && resourceAllocation.role.id,
          allocationStatus: ResourceAllocationStatus.Committed,
          load: resourceAllocation.load,
          projectUri,
          scheduleRules: overrideScheduleRules || {
            do: defaultScheduleRule.do,
            dateRange: {
              startDate: dateToMidnightUTCString(start),
              endDate: dateToMidnightUTCString(end)
            }
          }
        })
      });
    },
    [me, onCreateResourceAllocation, scale, smartFitResourceAllocation.id]
  );

  const handleEditResourceAllocation = useCallback(
    async ({
      smartFitResourceAllocation: allocation,
      overrideScheduleRules
    }) => {
      const {
        isAllocated,
        impactedTasks = [],
        asOfAllocation,
        ...resourceAllocation
      } = allocation;

      if (impactedTasks.length > 0) {
        await releaseTaskOwners(
          impactedTasks,
          resourceAllocation.user.userUri,
          resourceAllocation.requestedRoleUri
        );
      }

      if (asOfAllocation) {
        const {
          patchAllocation,
          newAllocation,
          onDialogClose
        } = asOfAllocation;

        onDialogClose();
        onPatchResourceAllocation({
          allocation: patchAllocation,
          project
        });

        await onCreateResourceAllocation({
          allocation: newAllocation
        });

        return;
      }

      onUpdateResourceAllocation({
        allocation: {
          ...resourceAllocation,
          scheduleRules: overrideScheduleRules,
          isAdjustedLoading: resourceAllocation.isAdjustedLoading || false,
          resourceRequestId: resourceAllocation.resourceRequestId || null
        }
      });
    },
    [
      onCreateResourceAllocation,
      onPatchResourceAllocation,
      onUpdateResourceAllocation,
      project,
      releaseTaskOwners
    ]
  );

  const handleAllocatedRoleChange = useCallback(
    ({ role: { role, resourceAllocation } }) => {
      if (role && role.isCreatable) return;

      onUpdateResourceAllocation({
        allocation: {
          ...resourceAllocation,
          requestedRoleUri: role ? role.id : null,
          role: role
            ? {
                ...role,
                uri: role.id
              }
            : null
        }
      });
    },
    [onUpdateResourceAllocation]
  );

  const handleAllocatedUserChange = useCallback(
    ({ resourceAllocation, user, impactedTasks }) => {
      setSmartFitResourceAllocation({
        ...resourceAllocation,
        user: {
          ...resourceAllocation.user,
          id: user.id,
          costCurrencyUri: user.currentCostRate
            ? user.currentCostRate.currency.id
            : null,
          costCurrency: user.currentCostRate
            ? user.currentCostRate.currency
            : null,
          costRate: user.currentCostRate ? user.currentCostRate.amount : null,
          displayText: user.displayText,
          userUri: user.id,
          primaryRoleCostRate:
            user.primaryRoleCostRate?.amount ||
            resourceAllocation.user.primaryRoleCostRate,
          user: {
            displayText: user.displayText,
            uri: user.id,
            roles: user.roles,
            slug: user.slug,
            permittedActionUris: user.permittedActionUris || []
          }
        },
        isAllocated: true,
        impactedTasks
      });
      openShowSmartFitDialogOrLoading();
    },
    [openShowSmartFitDialogOrLoading]
  );

  const getAllocationLoadingAndDateRange = useCallback(
    async ({ scheduleRules, userUri }) => {
      const { startDate, endDate } = getDateRangeFromScheduleRules(
        scheduleRules
      );

      const dateRange = {
        startDate,
        endDate
      };

      const { scheduledHours } = await getScheduleHoursForUserAndDateRange(
        userUri,
        dateRange
      );

      const allocatedHours =
        getTotalHours({
          scheduleRules
        }) || 0;

      const allocatedLoad = (allocatedHours / (scheduledHours || 1)) * 100;

      return {
        load: allocatedLoad,
        dateRange
      };
    },
    [getScheduleHoursForUserAndDateRange]
  );

  const handleAddAllocation = useCallback(
    ({ resourceAllocation, user, role, load, scheduleRules }) => {
      setSmartFitResourceAllocation({
        ...resourceAllocation,
        load,
        user: mapUserToAllocationUser(user),
        role,
        scheduleRules
      });
      openShowSmartFitDialogOrLoading();
    },
    [openShowSmartFitDialogOrLoading]
  );

  const handleNewMergeResourceAllocation = useCallback(
    async ({
      overrideScheduleRules,
      resourceAllocation: existingAllocation,
      smartFitResourceAllocation: allocation,
      isNewAllocation = allocation.isNewAllocation
    }) => {
      const { asOfAllocation, ...resourceAllocation } = allocation;

      if (asOfAllocation) {
        const {
          patchAllocation,
          newAllocation,
          onDialogClose
        } = asOfAllocation;

        onDialogClose();
        onPatchResourceAllocation({
          allocation: patchAllocation,
          project
        });

        const mergedAllocations = mergePeriodRulesIntoScheduleRules(
          existingAllocation.scheduleRules
        )(newAllocation.scheduleRules);
        const { dateRange, load } = await getAllocationLoadingAndDateRange({
          scheduleRules: mergedAllocations,
          userUri: existingAllocation.user.userUri
        });

        onUpdateResourceAllocation({
          allocation: {
            ...existingAllocation,
            load,
            startDate: dateRange.startDate,
            endDate: dateRange.endDate,
            scheduleRules: mergedAllocations,
            isAdjustedLoading: resourceAllocation.isAdjustedLoading || false,
            resourceRequestId: resourceAllocation.resourceRequestId || null
          }
        });

        return;
      }

      if (isNewAllocation) {
        const { dateRange, load } = await getAllocationLoadingAndDateRange({
          scheduleRules: overrideScheduleRules,
          userUri: existingAllocation.user.userUri
        });

        onUpdateResourceAllocation({
          allocation: {
            ...existingAllocation,
            load,
            startDate: dateRange.startDate,
            endDate: dateRange.endDate,
            scheduleRules: overrideScheduleRules,
            isAdjustedLoading: resourceAllocation.isAdjustedLoading || false,
            resourceRequestId: resourceAllocation.resourceRequestId || null
          }
        });

        return;
      }

      await onMergeResourceAllocation({
        allocationId: allocation.id,
        existingAllocationId: existingAllocation.id
      });
    },
    [
      onMergeResourceAllocation,
      onPatchResourceAllocation,
      onUpdateResourceAllocation,
      project,
      smartFitResourceAllocation.id
    ]
  );

  return {
    handleAddAllocation,
    createAllocationLoading,
    updateAllocationLoading,
    handleEditResourceAllocation,
    handleAddResourceAllocation,
    handleUserAndRoleChange,
    handleUserChange,
    handleAllocatedUserChange,
    setSmartFitResourceAllocation,
    handleAllocatedRoleChange,
    handleResourceChangeAndUpdateResourceAllocation,
    openSmartFitDialogOrLoadingState,
    closeShowSmartFitDialogOrLoading,
    handleNewMergeResourceAllocation,
    onSmartFitCancel,
    smartFitResourceAllocation
  };
};

export default useResourceAllocationChartEventHandlers;
